import {useContext} from 'react'

import {OrderProcessContext} from '../../../context/OrderProcessContext'

import {OrderChangeContextProps} from './OrderChangeContext.types'

/**
 * Hook to access order change context.
 * Provides information about whether an order can be modified.
 * Must be used within OrderChangeContextProvider.
 *
 * @returns Order change state with isChangeable boolean
 * @throws Error if used outside of OrderChangeContextProvider
 *
 * @example
 * // Basic usage - enabling/disabling form fields
 * function OrderForm() {
 *   const {isChangeable} = useOrderChangeContext()
 *
 *   return (
 *     <form>
 *       <Input disabled={!isChangeable} />
 *       <Button disabled={!isChangeable}>Save Changes</Button>
 *     </form>
 *   )
 * }
 *
 * @example
 * // Conditional rendering based on changeability
 * function OrderActions() {
 *   const {isChangeable} = useOrderChangeContext()
 *
 *   if (!isChangeable) {
 *     return <Alert>This order cannot be modified</Alert>
 *   }
 *
 *   return (
 *     <>
 *       <EditButton />
 *       <SaveButton />
 *     </>
 *   )
 * }
 */
export const useOrderChangeContext = () => {
  const context = useContext(OrderProcessContext)
  if (!context) {
    throw new Error('useOrderChangeContext must be used within OrderChangeContextProvider')
  }
  return context as OrderChangeContextProps
}
